import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useContent } from '../../hooks/useContent';

const steps = [
  { num: '01', title: 'Initial Enquiry', desc: 'Share your requirements — whether it\'s a new property, existing space, or specific challenges you\'re facing.', icon: '💬' },
  { num: '02', title: 'Space Analysis', desc: 'Detailed analysis of your space including floor plan, directions, and energy flow assessment.', icon: '🔍' },
  { num: '03', title: 'Vastu Evaluation', desc: 'Comprehensive Vastu evaluation identifying imbalances and areas for improvement.', icon: '📊' },
  { num: '04', title: 'Practical Remedies', desc: 'Result-oriented recommendations that are effective and easy to implement without major structural changes.', icon: '✨' },
  { num: '05', title: 'Follow-up Support', desc: 'Ongoing guidance to ensure positive transformation and lasting results in your space.', icon: '🤝' },
];

function TimelineStep({ step, index }: { step: typeof steps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const isLeft = index % 2 === 0;

  return (
    <div ref={ref} className="timeline-step" style={{ display: 'flex', justifyContent: isLeft ? 'flex-start' : 'flex-end', marginBottom: 48, position: 'relative' }}>
      {/* Center dot */}
      <motion.div
        className="timeline-dot"
        initial={{ scale: 0, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : {}}
        transition={{ delay: 0.2, type: 'spring', stiffness: 300 }}
        style={{
          position: 'absolute', left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 16, height: 16, borderRadius: '50%',
          background: '#E8871A', zIndex: 1,
          boxShadow: '0 0 20px rgba(232,135,26,0.6)'
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: 'absolute', inset: -4, borderRadius: '50%',
            border: '1px solid rgba(232,135,26,0.5)'
          }}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: isLeft ? -60 : 60 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        whileHover={{ scale: 1.02, boxShadow: '0 12px 40px rgba(232,135,26,0.12)' }}
        className="timeline-card"
        style={{
          width: '44%',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(232,135,26,0.15)',
          borderRadius: 12, padding: '28px 32px',
          cursor: 'default', transition: 'box-shadow 0.3s'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 24 }}>{step.icon}</span>
          <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 36, color: 'rgba(232,135,26,0.2)', lineHeight: 1 }}>
            {step.num}
          </p>
        </div>
        <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, marginBottom: 10, color: '#FDF6EC' }}>{step.title}</h3>
        <p style={{ fontSize: 14, lineHeight: 1.7, opacity: 0.7 }}>{step.desc}</p>
      </motion.div>
    </div>
  );
}

export default function ProcessTimeline() {
  const { content } = useContent();
  const p = content?.process;
  const list = p?.steps ?? steps;
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const lineHeight = useTransform(scrollYProgress, [0.1, 0.9], ['0%', '100%']);

  return (
    <section ref={sectionRef} style={{ padding: '100px 0', background: '#0D0D1F', overflow: 'hidden' }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 64 }}
        >
          <p className="section-eyebrow">{p?.eyebrow ?? 'How It Works'}</p>
          <h2 className="section-title">{p?.title ?? 'Our Consultation Process'}</h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 60 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ height: 2, background: 'linear-gradient(90deg, #E8871A, transparent)', margin: '20px auto' }}
          />
        </motion.div>

        <div style={{ position: 'relative' }}>
          {/* Animated vertical line */}
          <div className="timeline-line" style={{
            position: 'absolute', left: '50%', top: 0, bottom: 0,
            width: 1, background: 'rgba(232,135,26,0.1)',
            transform: 'translateX(-50%)'
          }}>
            <motion.div style={{
              width: '100%', height: lineHeight,
              background: 'linear-gradient(to bottom, #E8871A, rgba(201,84,122,0.5))',
              transformOrigin: 'top'
            }} />
          </div>

          {list.map((step: any, i: number) => (
            <TimelineStep key={i} step={step} index={i} />
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .timeline-step { justify-content: flex-start !important; padding-left: 32px; }
          .timeline-card { width: 100% !important; }
          .timeline-line { left: 12px !important; }
          .timeline-dot { left: 12px !important; }
        }
      `}</style>
    </section>
  );
}
